import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  enableIndexedDbPersistence, 
  onSnapshot,
  connectFirestoreEmulator,
  initializeFirestore as initFirestore
} from 'firebase/firestore';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getAnalytics } from 'firebase/analytics';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL
};

// Validate Firebase configuration
const validateFirebaseConfig = () => {
  const requiredFields = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId'
  ];

  const missingFields = requiredFields.filter(field => !firebaseConfig[field]);
  
  if (missingFields.length > 0) {
    throw new Error(`Missing required Firebase configuration fields: ${missingFields.join(', ')}. Please check your environment variables.`);
  }
};

// Initialize Firebase
let app;
try {
  validateFirebaseConfig();
  app = initializeApp(firebaseConfig);
  console.log('Firebase initialized successfully');
} catch (error) {
  console.error('Firebase initialization error:', error.message);
  throw error;
}

// Initialize Firestore with persistence settings
export const db = initFirestore(app, {
  // Use cache setting instead of enableIndexedDbPersistence
  cache: 'persistent'
});

// Initialize Auth with error handling
export const auth = getAuth(app);

// Enable offline persistence for Firestore with better error handling
// Using the new recommended approach instead of enableIndexedDbPersistence
const initializeFirestore = async () => {
  try {
    // No need to call enableIndexedDbPersistence as we're using the cache setting
    console.log('Firestore persistence enabled');
  } catch (err) {
    if (err.code === 'failed-precondition') {
      console.warn('Multiple tabs open, persistence can only be enabled in one tab at a time.');
    } else if (err.code === 'unimplemented') {
      console.warn('The current browser does not support persistence.');
    } else {
      console.error('Error enabling Firestore persistence:', err);
    }
  }
};

// Set auth persistence to LOCAL with better error handling
const initializeAuth = async () => {
  try {
    await setPersistence(auth, browserLocalPersistence);
    console.log('Auth persistence set to LOCAL');
  } catch (error) {
    console.error('Error setting auth persistence:', error);
    // Don't throw here, as this is not critical for app function
  }
};

// Initialize both Firestore and Auth
Promise.all([initializeFirestore(), initializeAuth()])
  .then(() => {
    console.log('Firebase services initialized successfully');
  })
  .catch((error) => {
    console.error('Error initializing Firebase services:', error);
  });

// Initialize Analytics only in production and if supported
let analytics = null;
if (process.env.NODE_ENV === 'production') {
  try {
    analytics = getAnalytics(app);
    console.log('Analytics initialized successfully');
  } catch (error) {
    console.warn('Analytics initialization failed:', error);
  }
}

// Export Firestore utility functions
export { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  onSnapshot, 
  analytics 
};
